import { baseApi } from "../../../../../core/data/rtk/baseQuery";
import { ApiResponseType } from "../../../../../core/data/rtk/apiResponseType";
import { UserUpdatePayload } from "../types/userRequest";

interface UserCredentialsPayload {
  username: string;
  password: string;
  role?: string;
  center_id: number;
}

interface ChangePasswordPayload {
  old_password: string;
  new_password: string;
}

interface UserDetails {
  id: number;
  username: string;
  role: string;
  status: string;
  student_id: number | null;
  center_id: number | null;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

interface UserCredentialsResponse {
  username: string;
}

interface GetUserResponse {
  user: UserDetails;
}
interface UserListResponse {
  items: UserDetails[];
  pageSize: number;
  currentPage: number;
  totalItems: number;
  totalPages: number;
}

interface UsernameCheckResponse {
  available: boolean;
}

export const centerUserDetailsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    saveUserCredentials: builder.mutation<
      ApiResponseType<UserCredentialsResponse>,
      UserCredentialsPayload
    >({
      query: (payload) => ({
        url: "users/save",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Users"],
    }),

    getCenterCredentials: builder.query<
      ApiResponseType<GetUserResponse>,
      number
    >({
      query: (id) => `users/get/${id}`,
      providesTags: ["Users"],
    }),

    getCenterUsers: builder.query<
      ApiResponseType<UserListResponse>,
      {
        centerId: number;
        page?: number;
        size?: number;
      }
    >({
      query: ({ centerId, page = 1, size = 10 }) =>
        `users/get-by-center/${centerId}?page=${page}&size=${size}`,
      providesTags: ["Users"],
    }),

    deleteUser: builder.mutation<ApiResponseType<void>, number>({
      query: (id) => ({
        url: `users/delete/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Users"],
    }),

    updateUser: builder.mutation<
      ApiResponseType<UserDetails>,
      { id: number; payload: Partial<UserUpdatePayload> }
    >({
      query: ({ id, payload }) => ({
        url: `users/update/${id}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Users"],
    }),

    checkUsername: builder.query<
      ApiResponseType<UsernameCheckResponse>,
      string
    >({
      query: (username) => ({
        url: `users/check-username`,
        method: "GET",
        params: { username },
      }),
      keepUnusedDataFor: 0,
    }),

    changePassword: builder.mutation<
      ApiResponseType<void>,
      { id: number; payload: ChangePasswordPayload }
    >({
      query: ({ id, payload }) => ({
        url: `users/change-password/${id}`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Users"],
    }),
  }),
});

export const {
  useSaveUserCredentialsMutation,
  useGetCenterCredentialsQuery,
  useGetCenterUsersQuery,
  useDeleteUserMutation,
  useUpdateUserMutation,
  useCheckUsernameQuery,
  useChangePasswordMutation,
} = centerUserDetailsApi;
